import { WorkOutlineOutlined as WorkOutlineOutlinedIcon } from 'components/icons';
import ListCreateButton from 'components/buttons/ListCreateButton';
import Loader from 'components/Loader';
import { FormattedDate } from 'components/misc/FormattedDate';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { DataTableColumnDef } from 'components/tables/data-table';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import TooltipHeader from 'components/tables/TooltipHeader';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useStartRankingsRefereeExportMutation } from 'store/api/exports';
import {
    useGetRankingsRefereeNominationsCountQuery,
    useLazyGetRankingsRefereeNominationsCountQuery,
    useLazyGetRankingsRefereeNominationsQuery,
} from 'store/api/rankingsReferees';
import { NominationListItem, RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { EXPORT_CONFIG } from 'types/exports';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { nominationName } from './nominationName';
import { NominationStatusControl } from './NominationStatusControl';
import { requestedThisYear, useRankingsRefereeFilterConfig } from './useRankingsRefereeFilterConfig';

const CLASSIFIERS = ['LEGAL_DIRECTORY'];
const EXPORT_PERMISSIONS = ['EXPORT.EFFORTS'];
const LINK_CLASS = 'entity-link text-fg-secondary new:text-muted-foreground w-fit no-underline hover:underline';

/** An unsortable column's title with its tooltip, and its width; the column picker reads the title from the meta. */
const column = (id: string, width: { widthWeight: number; minPx: number }) => ({
    header: () => (
        <TooltipHeader label={`rankings-referees.columns.${id}`} tooltip={`rankings-referees.columns.${id}-tooltip`} />
    ),
    enableSorting: false,
    meta: { pickerLabel: t(`rankings-referees.columns.${id}`), ...width },
});

type Props = {
    /** A person contact's tab: their nominations only, without the Referee column, the Company one first. */
    contactId?: string;
};

/** The Rankings referees list, one row a nomination, built as the BD efforts list is. */
const RankingsRefereesTable = ({ contactId }: Props): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();
    const isNewDesign = useIsNewDesign();
    const [fetchNominations, { data, isFetching }] = useLazyGetRankingsRefereeNominationsQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: CLASSIFIERS });
    const filterConfig = useRankingsRefereeFilterConfig({ classifiers, contactId });
    // The tab counts the person's nominations under its default filter "of" all of them.
    const { data: total } = useGetRankingsRefereeNominationsCountQuery(
        { filters: filterConfig.passiveFilter },
        { skip: !contactId }
    );

    const canExport = useHasPermissions(EXPORT_PERMISSIONS);
    const { isBusy: isExporting, triggerManualDownload } = useAsyncExport(useStartRankingsRefereeExportMutation, {
        entityType: 'RANKINGS_REFEREE',
        entityName: t('rankings-referees.nominations'),
        fileName: EXPORT_CONFIG.RANKINGS_REFEREE.fileName,
    });

    const columns = useMemo<DataTableColumnDef<NominationListItem>[]>(() => {
        const directory = (code: string) => getClassifierText(classifiers['LEGAL_DIRECTORY'], code);
        const all: DataTableColumnDef<NominationListItem>[] = [
            {
                ...column('referee', { widthWeight: 1.3, minPx: 180 }),
                id: 'referee',
                cell: ({ row }) => (
                    <PartyName party={row.original.referee} isLink variant="list" className="whitespace-normal" />
                ),
            },
            {
                // On the tab the person is known: the company is theirs, so it says so.
                ...column(contactId ? 'connected-company' : 'company', { widthWeight: 1.4, minPx: 200 }),
                id: 'company',
                cell: ({ row }) => {
                    const { companies } = row.original;
                    if (!companies.length) {
                        return '-';
                    }
                    // The new design draws each company as its contact chip; classic keeps the names as text.
                    return isNewDesign ? (
                        <div className="flex flex-wrap gap-1">
                            {companies.map((company) => (
                                <PartyName key={company.id} party={company} isLink variant="list" />
                            ))}
                        </div>
                    ) : (
                        <span className="whitespace-normal">{companies.map(({ name }) => name).join(', ')}</span>
                    );
                },
            },
            {
                ...column('directory', { widthWeight: 1.1, minPx: 150 }),
                id: 'directory',
                cell: ({ row }) => directory(row.original.legalDirectoryCode),
            },
            {
                ...column('edition', { widthWeight: 0.6, minPx: 96 }),
                id: 'edition',
                cell: ({ row }) => <span className="tabular-nums">{row.original.editionYear}</span>,
            },
            {
                ...column('practice-area', { widthWeight: 1.2, minPx: 160 }),
                id: 'practiceArea',
                cell: ({ row }) =>
                    row.original.practiceArea?.name ?? (
                        <span className="text-fg-secondary new:text-muted-foreground">
                            {t('rankings-referees.not-set')}
                        </span>
                    ),
            },
            {
                ...column('requested', { widthWeight: 1.1, minPx: 160 }),
                id: 'requested',
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        {/* A deleted colleague's name comes back blank: no link to them. */}
                        {row.original.requestedBy?.name.trim() ? (
                            <RecordLink
                                className="entity-link text-fg w-fit no-underline hover:underline"
                                to={`/employee/${row.original.requestedBy.id}`}
                                label={row.original.requestedBy.name}
                            />
                        ) : null}
                        <RouterLink className={LINK_CLASS} to={`/effort/${row.original.effortId}`}>
                            <FormattedDate value={row.original.requestedOn} />
                        </RouterLink>
                    </div>
                ),
            },
            {
                ...column('status', { widthWeight: 1, minPx: 164 }),
                id: 'status',
                cell: ({ row }) => (
                    <NominationStatusControl
                        id={row.original.id}
                        value={row.original.statusCode}
                        canUpdate={row.original.canUpdateStatus}
                        label={t('rankings-referees.status-of', {
                            name: row.original.referee.name,
                            nomination: nominationName(
                                directory(row.original.legalDirectoryCode),
                                row.original.editionYear,
                                row.original.practiceArea?.name
                            ),
                        })}
                    />
                ),
            },
        ];
        return contactId ? all.filter(({ id }) => id !== 'referee') : all;
    }, [classifiers, contactId, isNewDesign]);

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<NominationListItem>
            columnConfigId="rankings-referees"
            listKey="rankings-referees"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="rankings-referees.nominations"
            singularTextId="rankings-referees.nomination"
            countOf={contactId ? { messageId: 'rankings-referees.count-of', total: total?.count } : undefined}
            noEntriesId="rankings-referees.empty.title"
            noEntriesBodyId="rankings-referees.empty.body"
            noEntriesIcon={WorkOutlineOutlinedIcon}
            fetchData={fetchNominations}
            fetchCount={useLazyGetRankingsRefereeNominationsCountQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            disableUrlState={false}
            extraHeaderComponent={
                contactId ? (
                    <ListCreateButton
                        permissions={['CREATE.EFFORTS']}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() =>
                            navigateWithReturn(`/contact/${contactId}/effort/new?activityType=${RANKINGS_REFEREE_TYPE}`)
                        }
                        labelId="efforts.add-new-effort"
                        newLabelId="new-entity.bd-effort"
                    />
                ) : null
            }
            exportHookResults={
                canExport ? [{ labelKey: 'export', isBusy: isExporting, triggerManualDownload }] : undefined
            }
            initialSearchParams={contactId ? `qf=requestedOn=ge=${requestedThisYear().argument}` : undefined}
        />
    );
};

export default RankingsRefereesTable;
